import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import './HomePage.css';
import { clickable } from '../../utils/a11y';
import { useTranslation } from 'react-i18next';

const ProductionHomePage = () => {
  const { t } = useTranslation('production');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    orders: { active: 0, completed_today: 0, urgent: 0 },
    machines: { in_use_now: 0, active: 0, utilization_percent: 0 },
    inventory: { low_stock_count: 0 }
  });

  useEffect(() => {
    fetchProductionMetrics();

    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchProductionMetrics, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchProductionMetrics = async () => {
    try {
      const response = await apiFetch(getApiUrl('/api/production/metrics/overview'));
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching production metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="home-page">
      <div className="page-header">
        <h1>{t('productionHomePage.title')}</h1>
        <p className="welcome-text">{t('productionHomePage.welcome', { name: user?.full_name || user?.username })}</p>
      </div>

      <div className="stats-grid">
        <div className="stat-card" {...clickable(() => navigate('/production'))}>
          <div className="stat-icon">⚙️</div>
          <div className="stat-content">
            <h3>{t('productionHomePage.activeOrders')}</h3>
            <p className="stat-value">{loading ? '...' : stats.orders.active}</p>
            {!loading && stats.orders.urgent > 0 && (
              <p className="stat-subtitle">{t('productionHomePage.urgentPriority', { count: stats.orders.urgent })}</p>
            )}
          </div>
        </div>

        <div className="stat-card" {...clickable(() => navigate('/production'))}>
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>{t('productionHomePage.completedToday')}</h3>
            <p className="stat-value">{loading ? '...' : stats.orders.completed_today}</p>
          </div>
        </div>

        <div className="stat-card" {...clickable(() => navigate('/machine-schedules'))}>
          <div className="stat-icon">🏭</div>
          <div className="stat-content">
            <h3>{t('productionHomePage.machineUtilization')}</h3>
            <p className="stat-value">{loading ? '...' : `${stats.machines.utilization_percent}%`}</p>
            {!loading && (
              <p className="stat-subtitle">{t('productionHomePage.machinesInUse', { inUse: stats.machines.in_use_now, total: stats.machines.active })}</p>
            )}
          </div>
        </div>

        <div className="stat-card" {...clickable(() => navigate('/inventory'))}>
          <div className="stat-icon">📦</div>
          <div className="stat-content">
            <h3>{t('productionHomePage.materialStatus')}</h3>
            <p className="stat-value">{loading ? '...' : stats.inventory.low_stock_count}</p>
            {!loading && (
              <p className="stat-subtitle">{t('productionHomePage.lowStockItems')}</p>
            )}
          </div>
        </div>
      </div>

      <div className="quick-actions">
        <h2>{t('productionHomePage.quickActions')}</h2>
        <div className="action-buttons">
          <button onClick={() => navigate('/production')} className="action-btn">
            <span className="action-icon">🏭</span>
            {t('productionHomePage.actions.productionDashboard')}
          </button>
          <button onClick={() => navigate('/machine-schedules')} className="action-btn">
            <span className="action-icon">📅</span>
            {t('productionHomePage.actions.machineSchedules')}
          </button>
          <button onClick={() => navigate('/inventory')} className="action-btn">
            <span className="action-icon">🧱</span>
            {t('productionHomePage.actions.materialsInventory')}
          </button>
          <button onClick={() => navigate('/products')} className="action-btn">
            <span className="action-icon">📦</span>
            {t('productionHomePage.actions.viewBoms')}
          </button>
        </div>
      </div>

    </div>
  );
};

export default ProductionHomePage;